// src/services/servicesMunicipauxServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/services-municipaux`;

/* =========================
   Helpers
   ========================= */
// Récupère le token à chaque appel (utile après login/logout)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Construit une querystring propre
const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null) return;

    // Tableaux -> "a,b,c" (ex: categorie, type_service)
    if (Array.isArray(val)) {
      const joined = val
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
      if (joined) q.append(key, joined);
      return;
    }

    // Booléens -> "true"/"false"
    if (typeof val === "boolean") {
      q.append(key, val ? "true" : "false");
      return;
    }

    // Autres types
    const str = String(val).trim();
    if (str !== "") q.append(key, str);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

/* =========================
   API — Public
   ========================= */

// GET — Liste simple (actifs + publics, tri ordre_affichage/alpha)
export const getPublicServices = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllServices`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des services municipaux :", error);
    return [];
  }
};

// GET — Détail par slug (page publique)
export const getServiceBySlug = async (slug) => {
  if (!slug) return null;
  try {
    const safeSlug = encodeURIComponent(String(slug));
    const { data } = await axios.get(`${baseUrl}/slug/${safeSlug}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement service municipal par slug :", error);
    return null;
  }
};

// GET — Services par catégorie (public)
export const getServicesByCategorie = async (categorie) => {
  if (!categorie) return [];
  try {
    const safeCat = encodeURIComponent(String(categorie));
    const { data } = await axios.get(
      `${baseUrl}/categorie/${safeCat}`,
      { headers: authHeaders() }
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Erreur chargement services municipaux par catégorie :",
      error
    );
    return [];
  }
};

// GET — Sous-services / enfants d’un parent (public)
export const getChildrenByParent = async (parentId) => {
  if (!parentId) return [];
  try {
    const safeId = encodeURIComponent(String(parentId));
    const { data } = await axios.get(
      `${baseUrl}/parent/${safeId}`,
      { headers: authHeaders() }
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Erreur chargement sous-services municipaux par parent :",
      error
    );
    return [];
  }
};

/* =========================
   API — Admin / Back-office
   ========================= */

// GET — Recherche + filtres + pagination
// params: { q, categorie, type_service, is_active, is_public, with_gps, parent_id, page, limit, sort, dir }
export const searchServicesMunicipaux = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });

    // Normalisation du payload
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche services municipaux :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// GET — Alias explicite pour coller à la route/back (admin)
export const getAllServicesMunicipaux = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllServices`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Erreur lors du chargement des services municipaux (getAllServices) :",
      error
    );
    return [];
  }
};

// GET — Détail par ID (admin)
export const getServiceById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement service municipal par ID :", error);
    return null;
  }
};

// POST — Création
export const createServiceMunicipal = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création service municipal :", error);
    return null;
  }
};

// PUT — Remplacement total
export const updateServiceMunicipal = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour service municipal :", error);
    return null;
  }
};

// PATCH — Mise à jour partielle
export const patchServiceMunicipal = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch service municipal :", error);
    return null;
  }
};

// PATCH — Activer/Désactiver (is_active)
export const toggleServiceMunicipalActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(
      `${baseUrl}/${id}/toggle-active`,
      null,
      { headers: authHeaders() }
    );
    return data || null;
  } catch (error) {
    console.error("Erreur toggle actif service municipal :", error);
    return null;
  }
};

// PATCH — Rendre public / masquer (is_public)
export const toggleServiceMunicipalPublic = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(
      `${baseUrl}/${id}/toggle-public`,
      null,
      { headers: authHeaders() }
    );
    return data || null;
  } catch (error) {
    console.error("Erreur toggle public service municipal :", error);
    return null;
  }
};

// DELETE — Suppression
export const deleteServiceMunicipal = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression service municipal :", error);
    return false;
  }
};
